import React, { useRef, useState } from 'react';
import '../assets/styles/Contact.scss';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import SendIcon from '@mui/icons-material/Send';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

function Contact() {
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [message, setMessage] = useState<string>('');

  const [nameError, setNameError] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [messageError, setMessageError] = useState<boolean>(false);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const form = useRef<HTMLFormElement>(null);

  const validateForm = (): boolean => {
    const newNameError = name.trim() === '';
    const newEmailError = email.trim() === '';
    const newMessageError = message.trim() === '';
    
    setNameError(newNameError);
    setEmailError(newEmailError);
    setMessageError(newMessageError);
    
    return !newNameError && !newEmailError && !newMessageError;
  };

  const sendEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus('idle');

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('email', email);
      formData.append('message', message);

      const response = await fetch('https://formspree.io/f/mbdppedw', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setSubmitStatus('success');
        setName('');
        setEmail('');
        setMessage('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="contact">
      <div className="items-container">
        <div className="contact_wrapper">
          <h1>Contact Me</h1>
          <p>
            Have a project in mind? Let's discuss how I can help with your backend, 
            microservices, or payment system needs.
          </p>
          
         <Box
            ref={form}
            component="form"
            noValidate
            autoComplete="off"
            className='contact-form'
            onSubmit={sendEmail}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1.5rem',
            }}
          >
            <div className='form-flex' style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label htmlFor="name" style={{ display: 'block', marginBottom: '0.5rem', color: '#555555', fontWeight: 500 }}>
                  Your Name *
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="What's your name?"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    fontSize: '16px',
                    border: nameError ? '1px solid #d32f2f' : '1px solid #cccccc',
                    borderRadius: '4px',
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#5000ca';
                  }}
                  onBlur={(e) => {
                    if (!nameError) {
                      e.target.style.borderColor = '#cccccc';
                    }
                  }}
                />
                {nameError && (
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#d32f2f' }}>Please enter your name</p>
                )}
              </div>
              
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem', color: '#555555', fontWeight: 500 }}>
                  Email *
                </label>
                <input
                  id="email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="How can I reach you?"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    fontSize: '16px',
                    border: emailError ? '1px solid #d32f2f' : '1px solid #cccccc',
                    borderRadius: '4px',
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#5000ca';
                  }}
                  onBlur={(e) => {
                    if (!emailError) {
                      e.target.style.borderColor = '#cccccc';
                    }
                  }}
                />
                {emailError && (
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#d32f2f' }}>Please enter your email or phone number</p>
                )}
              </div>
            </div>
            
            <div>
              <label htmlFor="message" style={{ display: 'block', marginBottom: '0.5rem', color: '#555555', fontWeight: 500 }}>
                Message *
              </label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell me about your project, collaboration opportunity, or any questions..."
                rows={8}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  fontSize: '16px',
                  border: messageError ? '1px solid #d32f2f' : '1px solid #cccccc',
                  borderRadius: '4px',
                  backgroundColor: '#ffffff',
                  color: '#000000',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#5000ca';
                }}
                onBlur={(e) => {
                  if (!messageError) {
                    e.target.style.borderColor = '#cccccc';
                  }
                }}
              />
              {messageError && (
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#d32f2f' }}>Please enter your message</p>
              )}
            </div>
            
            {submitStatus === 'success' && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <AlertTitle>✓ Message Sent!</AlertTitle>
                Thanks for reaching out! I'll get back to you within 24-48 hours.
              </Alert>
            )}
            
            {submitStatus === 'error' && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>❌ Something went wrong</AlertTitle>
                Failed to send your message. Please email me directly at erick.muthomi@outlook.com or try again.
              </Alert>
            )}
            
            <Button 
              type="submit"
              variant="contained" 
              endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />} 
              disabled={isSubmitting}
              sx={{ 
                mt: 2, 
                backgroundColor: '#5000ca',
                '&:hover': {
                  backgroundColor: '#3a0098',
                },
                alignSelf: 'flex-start'
              }}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </Button>
          </Box>
        </div>
      </div>
    </div>
  );
}

export default Contact;